import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { toast } from "sonner";

const fields = [
  { name: "name", label: "Your Name", type: "text", placeholder: "Jane Doe" },
  {
    name: "email",
    label: "Email Address",
    type: "email",
    placeholder: "jane@company.com",
  },
  {
    name: "company",
    label: "Company (optional)",
    type: "text",
    placeholder: "Company name",
  },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitted(true);
    toast.success("Message sent — we'll be in touch shortly.");
  };

  return (
    <div data-testid="contact-page" className="min-h-screen bg-[#070707]">
      <section className="px-6 pt-40 pb-28 md:px-12 md:pt-52">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-16 md:grid-cols-12">
          {/* Left */}
          <div className="md:col-span-5">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#E83D16]"
            >
              [ Contact ]
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.7, 0, 0.2, 1] }}
              className="mt-6 font-display text-6xl leading-[0.9] tracking-tighter text-[#F5F5F0] md:text-8xl"
            >
              Let&apos;s talk.
            </motion.h1>
            <p className="mt-8 max-w-md font-body text-base leading-relaxed text-[#A1A1AA] md:text-lg">
              Partnerships, acquisitions, or joining the group — tell us what
              you&apos;re building and we&apos;ll get back to you.
            </p>
            <div className="mt-12 space-y-6">
              <div>
                <p className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">
                  Email
                </p>
                <a
                  href="mailto:hello@fairmountmedia.group"
                  className="link-sweep mt-2 inline-block font-body text-lg text-[#F5F5F0] hover:text-[#E83D16]"
                >
                  hello@fairmountmedia.group
                </a>
              </div>
              <div>
                <p className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">
                  Location
                </p>
                <p className="mt-2 font-body text-lg text-[#F5F5F0]">
                  Global &mdash; Remote First
                </p>
              </div>
            </div>
          </div>

          {/* Right - form */}
          <div className="md:col-span-6 md:col-start-7">
            <AnimatePresence mode="wait">
              {submitted ? (
                <motion.div
                  key="success"
                  data-testid="contact-success"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex min-h-[24rem] flex-col items-start justify-center border border-[#27272A] bg-[#121212] p-12"
                >
                  <span className="flex h-16 w-16 items-center justify-center bg-[#E83D16]">
                    <Check size={30} className="text-white" />
                  </span>
                  <h2 className="mt-8 font-display text-4xl tracking-tight text-[#F5F5F0]">
                    Message received.
                  </h2>
                  <p className="mt-4 font-body text-base text-[#A1A1AA]">
                    Thank you, {form.name.split(" ")[0] || "there"}. Our team
                    will be in touch shortly.
                  </p>
                  <button
                    data-testid="contact-reset-button"
                    onClick={() => {
                      setSubmitted(false);
                      setForm({
                        name: "",
                        email: "",
                        company: "",
                        message: "",
                      });
                    }}
                    className="link-sweep mt-8 font-mono-accent text-xs uppercase tracking-[0.2em] text-[#E83D16]"
                  >
                    Send another message
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  data-testid="contact-form"
                  onSubmit={handleSubmit}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="space-y-10"
                >
                  {fields.map((f) => (
                    <div key={f.name}>
                      <label
                        htmlFor={f.name}
                        className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#A1A1AA]"
                      >
                        {f.label}
                      </label>
                      <input
                        id={f.name}
                        name={f.name}
                        type={f.type}
                        value={form[f.name]}
                        onChange={handleChange}
                        placeholder={f.placeholder}
                        data-testid={`contact-input-${f.name}`}
                        className="mt-3 w-full border-0 border-b border-[#27272A] bg-transparent py-3 font-body text-lg text-[#F5F5F0] placeholder:text-[#52525b] focus:border-[#E83D16] focus:outline-none focus:ring-0"
                      />
                    </div>
                  ))}
                  <div>
                    <label
                      htmlFor="message"
                      className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#A1A1AA]"
                    >
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project..."
                      data-testid="contact-input-message"
                      className="mt-3 w-full resize-none border-0 border-b border-[#27272A] bg-transparent py-3 font-body text-lg text-[#F5F5F0] placeholder:text-[#52525b] focus:border-[#E83D16] focus:outline-none focus:ring-0"
                    />
                  </div>
                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    className="btn-editorial group inline-flex items-center gap-3 border border-[#F5F5F0] px-10 py-5 font-mono-accent text-xs uppercase tracking-[0.2em] text-[#F5F5F0] hover:text-white"
                  >
                    Send Message
                    <ArrowUpRight
                      size={16}
                      className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                    />
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>
    </div>
  );
}
